import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 4.2 visual preview at /preview/lesson/4.2
 * "Decide what matters: the editing move"
 */

export default function LessonPreviewPage4_2() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 4 · Doing the work AI cannot do · Lesson 4.2
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        Decide what matters: the editing move
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Judgment · Voice · Character</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        3 through-line drills
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="Two essays. Same student. Same week. One gets remembered.">
      <p className="mt-6 text-text/90">
        A college admissions reader has been at the desk for six hours. Every
        essay starts to blur. Then two come through from the same applicant.
      </p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
            Essay A · The list
          </p>
          <p className="mt-3 text-text/90">
            500 words. Covers debate, two clubs, a part-time job, a service trip,
            a family member who got sick, a class that was hard. Every paragraph
            ends with a lesson learned. Every paragraph is well-written. The reader
            cannot remember what it was about ten minutes later.
          </p>
        </div>
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            Essay B · The through-line
          </p>
          <p className="mt-3 text-text/90">
            500 words. About one summer working at a deli. One owner. One
            customer who came in every Tuesday. The lesson is buried, not stated.
            The reader is still thinking about it the next morning while making
            coffee.
          </p>
        </div>
      </div>
      <p className="mt-6 text-text/90">
        Same student. Same facts available to use. The difference is not skill.
        The difference is that the second essay decided what it was about and
        cut everything else. Most of writing is that one decision.
      </p>
      <p className="mt-4 text-text/90">
        AI cannot make that decision for you. AI will happily write essay A all
        day. Essay A is what AI defaults to when nobody is deciding.
      </p>
    </Section>
  );
}

/* --- Section 2: From last time --- */
function FromLastTime() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · Lesson 4.1" title="Three questions on the five moves.">
      <p className="mt-6 text-text/90">
        Three questions on yesterday. No looking back.
      </p>
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="Define Human-in-the-loop in one sentence." />
        <ReviewItem n="02" q="Name all five moves only you can make." />
        <ReviewItem n="03" q="Why can AI not do any of them, in one sentence?" />
      </ol>
      <p className="mt-6 text-sm text-muted">
        Today goes deep on move 01: deciding what matters. The other four still
        apply, but this is the one with its own vocab.
      </p>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90">{q}</span>
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · The editing move" title="One thing the work is about. Everything else serves it or gets cut.">
      <Subhead n={1} title="The vocab" />
      <p className="mt-4 text-text/90">
        Editors and writers have a word for the one thing a piece of work is
        about. Once you know the word, you can use it on your own drafts.
      </p>
      <VocabCard
        term="Mode collapse"
        definition="What happens when AI defaults to the safest, most average version of a piece of writing: a list of true things, with no one thing said loud. Mode collapse is not a bug you can prompt your way out of. It is the way generative models behave when nobody is deciding what the work is about. The through-line is the human move that fights it."
        analogy="A jukebox set to autoplay will eventually land on the most-played song of the decade. Statistically safe. Nobody requested it. Nobody hates it. Nobody remembers it either. AI writing without a through-line is autoplay. The fix is not a better jukebox. The fix is somebody picking the song."
        memoryHook="Lists are AI's autoplay. The through-line is you choosing the song."
        why="Mode collapse is why every AI-drafted essay starts to sound like every other AI-drafted essay. Teachers can feel it. Admissions readers can feel it. Recruiters can feel it. The students who beat it are the ones who decide what their one thing is and cut everything else. That decision cannot come from the model."
      />

      <Subhead n={2} title="Three signs you do not have one yet" className="mt-12" />
      <p className="mt-4 text-text/90">
        Most drafts are missing a through-line and the writer cannot tell. Three
        diagnostics. If any of these is true, the work is a topic, not an
        argument.
      </p>
      <SignsGrid />

      <Subhead n={3} title="The cut 30% exercise" className="mt-12" />
      <p className="mt-4 text-text/90">
        The fastest way to find a through-line in a draft you already have:
        force a cut. Almost every first draft is at least 30% longer than the
        work needs. The 30% you cut tells you what the work is actually about.
      </p>
      <CutExercise />

      <Subhead n={4} title="How AI gets used here without taking over" className="mt-12" />
      <p className="mt-4 text-text/90">
        AI can speed this move up if you keep the decisions on your side. Two
        prompts that help. One that hurts.
      </p>
      <PromptsGrid />
    </Section>
  );
}

const SIGNS = [
  {
    n: '01',
    label: 'The swap test fails',
    body: 'You can swap any paragraph order and nothing important breaks. A piece with a through-line has a sequence the writer chose for a reason. A piece without one is a stack of paragraphs that happen to be near each other.',
  },
  {
    n: '02',
    label: 'Every paragraph is interesting',
    body: 'You read each paragraph and they are all fine. They do not add up to anything though. Lots of small good without a big point. This is the most common failure mode of any draft an AI helped with.',
  },
  {
    n: '03',
    label: 'You cannot say it out loud',
    body: 'A friend asks “what is the essay about?” You take three sentences to answer. If you cannot say it in one sentence to a real person who is not reading it, the reader cannot find it either.',
  },
];

function SignsGrid() {
  return (
    <div className="mt-6 grid gap-4 sm:grid-cols-3">
      {SIGNS.map((s) => (
        <div key={s.n} className="rounded-lg border border-rose-500/40 bg-rose-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
            {s.n} · {s.label}
          </p>
          <p
            className="mt-3 text-sm text-text/85"
            dangerouslySetInnerHTML={{ __html: s.body }}
          />
        </div>
      ))}
    </div>
  );
}

function CutExercise() {
  return (
    <div className="mt-6 rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
        The move
      </p>
      <ol className="mt-4 space-y-3 text-text/90">
        <li className="flex gap-3">
          <span className="font-mono text-emerald-300 shrink-0">01</span>
          <span>
            Open your draft. Read it once, straight through, no edits.
          </span>
        </li>
        <li className="flex gap-3">
          <span className="font-mono text-emerald-300 shrink-0">02</span>
          <span>
            Find the one sentence in the entire draft that you would not delete
            if you had to lose everything else. Highlight it. That is your
            candidate through-line.
          </span>
        </li>
        <li className="flex gap-3">
          <span className="font-mono text-emerald-300 shrink-0">03</span>
          <span>
            Go paragraph by paragraph. For each paragraph, ask out loud: does
            this advance the highlighted sentence? If yes, keep it. If no, cut
            it. No softening, no “but it took me a while to write.”
            Cut it.
          </span>
        </li>
        <li className="flex gap-3">
          <span className="font-mono text-emerald-300 shrink-0">04</span>
          <span>
            Total your cuts. If you cut less than 30%, you flinched. Go again.
            The first round is rarely brutal enough.
          </span>
        </li>
        <li className="flex gap-3">
          <span className="font-mono text-emerald-300 shrink-0">05</span>
          <span>
            Read what is left out loud. It will be shorter, sharper, and more
            obviously about one thing. If it is not, your highlighted sentence
            was the wrong one. Pick a different one. Run it again.
          </span>
        </li>
      </ol>
    </div>
  );
}

const PROMPTS = [
  {
    accent: 'emerald' as const,
    title: 'Prompt that helps · Find the buried lead',
    body: '“Here is my draft. Find the one sentence in it that, if you only had that sentence, would still tell the reader what this is about. Quote it back to me. Do not rewrite anything.”',
    why: 'AI is good at pattern-spotting. You are still making the decision: do you agree that is the line, or is it a different one. AI does not get to choose.',
  },
  {
    accent: 'emerald' as const,
    title: 'Prompt that helps · The swap test',
    body: '“Here is my draft. Reorder the paragraphs in three different sequences and tell me which sequence works best and why. Do not change any words inside the paragraphs.”',
    why: 'If three different orders all feel fine, the draft has no real sequence. That is signal. You then decide what the right order would be if there were one.',
  },
  {
    accent: 'rose' as const,
    title: 'Prompt that hurts · Tighten this',
    body: '“Here is my draft. Make it tighter.”',
    why: 'AI will cut words without cutting paragraphs. You end up with the same shapeless piece in fewer words. The cut you actually need is structural, not cosmetic. Only you can decide which paragraphs do not belong.',
  },
];

function PromptsGrid() {
  return (
    <div className="mt-6 space-y-4">
      {PROMPTS.map((p, i) => {
        const isEmerald = p.accent === 'emerald';
        const borderCls = isEmerald ? 'border-emerald-500/40' : 'border-rose-500/40';
        const bgCls = isEmerald ? 'bg-emerald-500/[0.04]' : 'bg-rose-500/[0.04]';
        const labelCls = isEmerald ? 'text-emerald-300' : 'text-rose-300';
        return (
          <div key={i} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
              {p.title}
            </p>
            <p
              className="mt-3 text-text/90 italic"
              dangerouslySetInnerHTML={{ __html: p.body }}
            />
            <p className="mt-3 text-sm text-text/80">{p.why}</p>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 4: Your turn --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Spot the through-line" title="Three drafts. Pick the sentence that should be the opening.">
      <p className="mt-6 text-text/90">
        For each draft, the through-line is buried inside. Pick the sentence that
        should become the opening line of the rewrite. Same words, different
        position. The opening is the through-line said out loud.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <ThroughLineDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

type DrillSpec = {
  n: string;
  context: string;
  sentences: { key: string; text: string }[];
  correct: string;
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    context:
      'A 300-word piece about a student’s summer job at an ice cream shop. Pick which sentence should open it.',
    sentences: [
      { key: 'a', text: 'I worked at an ice cream shop in my neighborhood last summer.' },
      { key: 'b', text: 'The shop had four flavors that we made ourselves and twelve we bought in tubs.' },
      { key: 'c', text: 'I learned that the people who tipped were almost never the ones who said please.' },
      { key: 'd', text: 'My boss was a 60-year-old woman who had owned the shop for thirty years.' },
    ],
    correct: 'c',
    verdict:
      'Option C. It is the only sentence that promises an actual argument. The others are setup. The piece is not about the shop. The piece is about a thing the writer noticed about people while standing at a register. Open with the noticing, not the location.',
  },
  {
    n: '02',
    context:
      'A reflection on the school cross country season. The writer was a JV runner who got moved up to varsity mid-season.',
    sentences: [
      { key: 'a', text: 'Our team finished third at the district meet this year.' },
      { key: 'b', text: 'I started the season running JV and ended it in the varsity top seven.' },
      { key: 'c', text: 'The hardest part was not the races. It was deciding whether I had earned the spot or someone else had lost it.' },
      { key: 'd', text: 'My coach pulled me aside after a Tuesday practice and told me I was moving up that Saturday.' },
    ],
    correct: 'c',
    verdict:
      'Option C. Sentences A, B, and D are facts. C is a thought. The reflection is about an internal struggle, not the finish or the conversation after practice. The opening should be the thing the reader has not heard before. That is the one.',
  },
  {
    n: '03',
    context:
      'A short essay about helping take care of a grandparent during a recent illness.',
    sentences: [
      { key: 'a', text: 'My grandfather has been sick since the fall.' },
      { key: 'b', text: 'The first time I changed his shirt, neither of us spoke for the rest of the afternoon.' },
      { key: 'c', text: 'I drove to his house three times a week to help my grandmother.' },
      { key: 'd', text: 'Caregiving has taught me a lot about responsibility.' },
    ],
    correct: 'b',
    verdict:
      'Option B. Sentence D is the AI default and a trap: a lesson stated, not earned. Sentence B is a specific moment that contains the whole essay inside it. The reader who reads B keeps reading. The reader who reads D has read it before.',
  },
];

function ThroughLineDrill({ drill }: { drill: DrillSpec }) {
  const [picked, setPicked] = useState<string | null>(null);
  const correctSentence = drill.sentences.find((s) => s.key === drill.correct);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · The context
      </p>
      <p className="mt-3 text-text/90">{drill.context}</p>
      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Which sentence should open the rewrite?
        </p>
        <div className="mt-3 space-y-2">
          {drill.sentences.map((s) => {
            const isPicked = picked === s.key;
            const isCorrect = s.key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'w-full rounded-md border border-line bg-elevated p-3 text-left text-sm text-text/85 hover:border-amber';
            if (shouldShowCorrect)
              cls =
                'w-full rounded-md border border-emerald-500 bg-emerald-500/[0.08] p-3 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'w-full rounded-md border border-rose-500 bg-rose-500/[0.08] p-3 text-left text-sm text-text';
            return (
              <button
                key={s.key}
                className={cls}
                onClick={() => setPicked(s.key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Option {s.key.toUpperCase()}
                </span>
                <span className="mt-1 block">{s.text}</span>
              </button>
            );
          })}
        </div>
        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · Option {picked?.toUpperCase()}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That sentence is not the through-line. See the correct answer
              below.
            </p>
          </div>
        )}
        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              Through-line · Option {drill.correct.toUpperCase()}
            </p>
            <p className="mt-2 text-sm text-text/80 italic">
              “{correctSentence?.text}”
            </p>
            <p className="mt-3 text-sm text-text/90">{drill.verdict}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define through-line in one sentence." answer="The one thing a piece of work is about, stated in a single sentence the writer can say out loud; every paragraph either advances it or gets cut." />
        <QuickCheckItem n="Q2" q="Name the three signs a draft does not have a through-line." answer="The swap test fails (any paragraph order works), every paragraph is interesting but they do not add up, you cannot say the point in one sentence out loud." />
        <QuickCheckItem n="Q3" q="What is the cut 30% rule and why 30%?" answer="Force yourself to cut at least 30% of your first draft. The 30% you cut is what the work is not about. If you flinch and cut less, the through-line never sharpens." />
        <QuickCheckItem n="Q4" q="A friend asks what your essay is about. You take three sentences. What is missing?" answer="The through-line. If you cannot say it in one sentence to a real person who is not reading it, the reader cannot find it either." />
        <QuickCheckItem n="Q5" q="Why is “tighten this” the wrong AI prompt for finding a through-line?" answer="It cuts words without cutting paragraphs. You end up with the same shapeless piece in fewer words. The cut you actually need is structural and only you can make it." />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. Run it on every draft.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 4.2 · Lock card · Decide what matters
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          Most of writing is deciding what to cut. The through-line is what you
          keep. AI does not know which sentence is yours to keep.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <ChecksTile
            n="01"
            name="Say it out loud"
            body="If you cannot say the point in one sentence to a real friend, you do not have a through-line. You have a topic."
          />
          <ChecksTile
            n="02"
            name="Swap test"
            body="If any paragraph order works, the piece has no sequence. The sequence is part of the through-line."
          />
          <ChecksTile
            n="03"
            name="Cut 30%"
            body="If you did not cut at least 30%, you flinched. The 30% you cut tells you what the work is not about."
          />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The handoff to the next move
          </p>
          <p className="mt-2 text-text/90">
            Once the through-line is locked, lesson 4.3 is how you make it
            specific. A clear through-line said in general terms is still flat.
            Through-line + particular detail is what readers actually remember.
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          AI generates length. You decide direction. Direction wins.
        </p>
      </div>
    </Section>
  );
}

function ChecksTile({ n, name, body }: { n: string; name: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 4.3
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            Notice what is specific: the seeing move
          </h3>
          <p className="mt-3 text-sm text-text/85">
            AI generalizes. You particularize. Vocab: Particular. Where to mine
            for details only you have. The rewrite that turns an AI sentence
            into a sentence only you could write.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 4.2 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p className="mt-3 border-t border-line pt-3 text-sm text-text/85">
          {answer}
        </p>
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    '4.1 review',
    'The editing move',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
